import './accounts.css';
import { useEffect } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Label } from '../../components/Label';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { AnfragenBar } from '../anfragen/AnfragenBar';
import { AnfragenTab } from '../anfragen/AnfragenTab';
import { BelegeBars } from '../belege/BelegeBars';
import { BelegeTab } from '../belege/BelegeTab';
import { BwaTab } from '../bwa/BwaTab';
import { BriefeBar } from '../briefe/BriefeBar';
import { BriefeTab } from '../briefe/BriefeTab';
import { ChatBar } from '../chat/ChatBar';
import { ChatTab } from '../chat/ChatTab';
import { useChatUnreadForClient } from '../chat/useChatUnread';
import { HochladenTab } from '../dokumente/HochladenTab';
import { UstvaTab } from '../fristen/UstvaTab';
import { LohnBar } from '../lohn/LohnBar';
import { LohnTab } from '../lohn/LohnTab';
import { RechnungenTab } from '../rechnungen/RechnungenTab';
import { addressLine, AREAS } from './labels';
import { EndedBanner } from './MandatBeenden';
import { StammdatenTab } from './StammdatenTab';
import type { ClientDetail } from './types';

type Tab = 'chat' | 'belege' | 'ustva' | 'bwa' | 'hochladen' | 'briefe' | 'anfragen' | 'lohn' | 'rechnungen' | 'stammdaten';

// Mandantenansicht mit Tabs. Rechte prüft der Server; hier nur passende Tabs zeigen.
export function MandantDetail() {
  const { id = '', tab } = useParams();
  const navigate = useNavigate();
  const detail = useApi<ClientDetail>(`/api/kanzlei/clients/${id}`, { live: ['clients'], clientId: id });
  const unread = useChatUnreadForClient(id);
  const toast = useToast();
  const name = detail.data?.client.name;
  // Nur wenn der Zugriff wirklich weg ist (nicht mehr zugewiesen, gelöscht), zurück zur Liste. Ein kurzer Fehler beim
  // Neuladen (Netz, Neustart des Servers) lässt die Seite und ein offenes Formular stehen.
  const noAccess = detail.errorStatus === 403 || detail.errorStatus === 404;

  useEffect(() => {
    if (name) document.title = `${name} · Elbcons Kanzlei`;
  }, [name]);

  useEffect(() => {
    if (noAccess && detail.error) toast(detail.error);
  }, [noAccess, detail.error, toast]);

  if (noAccess) return <Navigate to="/kanzlei/mandanten" replace />;
  if (!detail.data) {
    if (!detail.error) return <div className="spinner" aria-label="Lädt" />;
    return (
      <div className="lock" role="alert">
        <span style={{ flex: 1 }}>{detail.error}</span>
        <button type="button" className="btn sm" onClick={() => void detail.reload()}>
          Erneut laden
        </button>
      </div>
    );
  }
  const d = detail.data;
  if (d.client.status === 'pending' || d.client.status === 'rejected') {
    return <Navigate to={`/kanzlei/registrierungen/${id}`} replace />;
  }

  const tabs: { key: Tab; label: string; short?: string }[] = [
    { key: 'chat', label: 'Chat' },
    ...(d.access.fibu ? [{ key: 'belege' as Tab, label: 'Belege vom Mandanten', short: 'Belege' }] : []),
    ...(d.access.fibu && d.client.country === 'DE' ? [{ key: 'ustva' as Tab, label: 'UStVA' }] : []),
    ...(d.access.fibu ? [{ key: 'bwa' as Tab, label: 'BWA' }] : []),
    ...(d.access.fibu || d.access.lohn ? [{ key: 'hochladen' as Tab, label: 'Hochladen' }] : []),
    ...(d.access.fibu || d.access.lohn ? [{ key: 'briefe' as Tab, label: 'Briefe' }] : []),
    ...(d.access.fibu || d.access.lohn ? [{ key: 'anfragen' as Tab, label: 'Anfragen' }] : []),
    ...(d.access.lohn ? [{ key: 'lohn' as Tab, label: 'Lohn' }] : []),
    ...(d.access.fibu ? [{ key: 'rechnungen' as Tab, label: 'Rechnungen' }] : []),
    // „Stammdaten“ ist am Handy zu lang für einen Reiter
    { key: 'stammdaten', label: 'Stammdaten', short: 'Daten' },
  ];
  const current: Tab = tabs.some((t) => t.key === tab)
    ? (tab as Tab)
    : d.access.fibu
      ? 'belege'
      : d.access.lohn
        ? 'lohn'
        : 'chat';

  // Bereich des angemeldeten Mitarbeiters: „FiBu“, „Lohn“ oder „FiBu und Lohn“
  const mine = [d.access.fibu && 'FiBu', d.access.lohn && 'Lohn'].filter(Boolean).join(' und ');
  // Offene Bereiche (nur für den Admin sichtbar): leer oder Person gibt es nicht mehr
  const missing = AREAS.filter((a) => d.missing[a.key]);
  const address = addressLine(d.client);

  return (
    <>
      <div className="crumb">
        <Link to="/kanzlei/mandanten">Mandanten</Link> › {d.client.name}
      </div>
      {detail.error && (
        <div className="lock" role="status">
          <span style={{ flex: 1 }}>Aktualisieren hat nicht geklappt. Die Angaben sind vielleicht nicht mehr aktuell.</span>
          <button type="button" className="btn sm" onClick={() => void detail.reload()}>
            Erneut laden
          </button>
        </div>
      )}
      <div className="head">
        <div>
          <h1>{d.client.name}</h1>
          <p>
            {address && (
              <>
                {address}
                <br />
              </>
            )}
            Mandanten-Nr. {d.client.number} · {d.client.contactName} · FiBu: {d.fibu?.name ?? 'offen'} · Lohn:{' '}
            {d.lohn?.name ?? 'offen'}
            {!d.access.isAdmin && mine && (
              <>
                {' · '}
                <b>Ihr Bereich: {mine}</b>
              </>
            )}
          </p>
        </div>
      </div>
      <EndedBanner detail={d} reload={detail.reload} />
      {d.access.isAdmin && d.client.status === 'active' && missing.length > 0 && (
        <div className="lock missbar" role="status">
          <span>
            <b>Zuständigkeit fehlt:</b>{' '}
            {missing
              .map((a) => a.label + (d.missing[a.key] === 'gone' ? ' (die bisherige Person gibt es nicht mehr)' : ''))
              .join(', ')}
            . Bis zur Zuweisung landet alles aus diesem Bereich nur bei Ihnen.
          </span>
          {current !== 'stammdaten' && (
            <button
              type="button"
              className="btn sm"
              onClick={() => navigate(`/kanzlei/mandanten/${id}/stammdaten`, { state: { assign: true } })}
            >
              Jetzt zuweisen
            </button>
          )}
        </div>
      )}
      <div className={`tabs${tabs.length > 4 ? ' many' : ''}`} role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={current === t.key}
            onClick={() => navigate(`/kanzlei/mandanten/${id}/${t.key}`)}
          >
            <Label text={t.label} short={t.short} />
            {t.key === 'chat' && unread > 0 && current !== 'chat' && <span className="unread"> {unread}</span>}
          </button>
        ))}
      </div>
      {/* Hinweisleisten dieses Mandanten unter Name und Reitern, damit am Handy zuerst zu sehen ist, welcher Mandant offen ist */}
      <ChatBar clientId={id} />
      <BelegeBars clientId={id} />
      <LohnBar clientId={id} />
      {/* im Reiter „Briefe“ bzw. „Anfragen“ stehen dieselben Einträge schon in der Liste */}
      {current !== 'briefe' && <BriefeBar clientId={id} />}
      {current !== 'anfragen' && <AnfragenBar clientId={id} />}
      {current === 'chat' && <ChatTab clientId={id} />}
      {current === 'belege' && <BelegeTab clientId={id} />}
      {current === 'ustva' && <UstvaTab clientId={id} />}
      {current === 'bwa' && <BwaTab clientId={id} />}
      {current === 'hochladen' && <HochladenTab clientId={id} />}
      {current === 'briefe' && <BriefeTab clientId={id} access={{ fibu: d.access.fibu, lohn: d.access.lohn }} />}
      {current === 'anfragen' && <AnfragenTab clientId={id} both={d.access.fibu && d.access.lohn} />}
      {current === 'lohn' && <LohnTab clientId={id} />}
      {current === 'rechnungen' && <RechnungenTab clientId={id} contact={d.client.contactName || d.client.name} />}
      {current === 'stammdaten' && <StammdatenTab detail={d} reload={detail.reload} />}
    </>
  );
}
